/**
 * Viewport tracker
 *
 * Maintains two related sources of truth for the *visible* viewport height:
 *
 * - `layout.viewportHeight` / `--app-vh` — the LIVE visible height. Always
 *   tracks the real visualViewport, including on-screen-keyboard shrinks.
 *   Chat and video sizing (layoutAdjustment.js) read this so they correctly
 *   shrink to fit above an open keyboard, the same way a native chat app does.
 * - `layout.materialViewportHeight` / `--app-vh-material` — the MATERIAL
 *   signal. Mirrors the live height except while a text input is focused
 *   (on-screen keyboard presumed open), when it freezes at its last
 *   pre-keyboard value. PDFMaterial/DiscoveryMaterial/the material mixin read
 *   this instead, because their mobile-branch formulas subtract the video
 *   column's height a second time on top of the base height — if that base
 *   is already keyboard-shrunk, the material panel collapses to a sliver.
 *   See .kiro/specs/lesson-room-viewport-keyboard/design.md.
 *
 * On mobile Chrome, `window.innerHeight` reports the layout viewport (the
 * tallest, address-bar-hidden height) and does not react when the address bar
 * shows/hides. `window.visualViewport.height` reports the actually-visible
 * region and fires fine-grained resize/scroll events as the bar animates, so
 * it is preferred when available.
 */

/**
 * Compute the current visible viewport height.
 *
 * @return {number} visualViewport height when available, else innerHeight
 */
export function computeViewportHeight() {
  if (window.visualViewport && window.visualViewport.height) {
    return window.visualViewport.height;
  }
  return window.innerHeight;
}

// Any element that can open an on-screen keyboard. Deliberately generic
// (not scoped to the chat input) so any current or future text field in the
// lesson room is covered without per-component wiring — see
// .kiro/specs/lesson-room-viewport-keyboard/design.md.
const TEXT_INPUT_SELECTOR = 'input[type="text"], textarea, [contenteditable="true"]';

/**
 * Whether a focus event target is a text-like input that can open the
 * on-screen keyboard.
 *
 * @param {EventTarget} target
 * @return {boolean}
 */
function isTextInputTarget(target) {
  return !!(target && typeof target.matches === 'function' && target.matches(TEXT_INPUT_SELECTOR));
}

// iOS Safari's keyboard show/dismiss is an animation, and the visualViewport
// resize event that reports the fully-settled height can lag that animation
// on either end, or in some iOS versions not fire again at all once it
// completes. An immediate resync on focusin/focusout/visibilitychange can
// therefore capture a mid-animation (transitional) height. Re-checking a few
// more times over the following moment catches the settled value even when
// the immediate resync was stale. Applied symmetrically on open (LIVE_RESYNC)
// and resume (MATERIAL_RESYNC), since nothing about the animation-lag is
// direction-specific. See .kiro/specs/lesson-room-viewport-keyboard/design.md.
const LIVE_RESYNC_RETRY_DELAYS_MS = [150, 400];
const MATERIAL_RESYNC_RETRY_DELAYS_MS = [150, 400];

/**
 * Create a viewport tracker bound to a Vuex store instance.
 *
 * @param {import('vuex').Store} store
 * @return {{ start: Function, stop: Function, apply: Function, onChange: Function }}
 */
export function createViewportTracker(store) {
  let frame = null;
  let materialRetryTimeoutIds = [];
  let liveRetryTimeoutIds = [];

  // While true, the material signal stops tracking new heights. Set while a
  // text input is focused (on-screen keyboard presumed open). The live
  // signal (layout.viewportHeight/--app-vh) is NEVER frozen — only the
  // material-specific signal is.
  let materialFrozen = false;

  const clearMaterialRetries = () => {
    materialRetryTimeoutIds.forEach(id => window.clearTimeout(id));
    materialRetryTimeoutIds = [];
  };

  const clearLiveRetries = () => {
    liveRetryTimeoutIds.forEach(id => window.clearTimeout(id));
    liveRetryTimeoutIds = [];
  };

  // Commit the live height to the store and CSS, deduping no-ops.
  const applyLive = (height) => {
    if (height === store.state.layout.viewportHeight) {
      return;
    }
    store.commit('layout/setViewportHeight', height);
    document.documentElement.style.setProperty('--app-vh', height + 'px');
  };

  // Commit the material-only height, deduping no-ops. Suppressed entirely
  // while materialFrozen.
  const applyMaterial = (height) => {
    if (materialFrozen) {
      return;
    }
    if (height === store.state.layout.materialViewportHeight) {
      return;
    }
    store.commit('layout/setMaterialViewportHeight', height);
    document.documentElement.style.setProperty('--app-vh-material', height + 'px');
  };

  const apply = () => {
    frame = null;
    const height = Math.round(computeViewportHeight());
    applyLive(height);
    applyMaterial(height);
  };

  // Coalesce bursts of resize/scroll events into one update per frame.
  const onChange = () => {
    if (frame !== null) {
      return;
    }
    frame = window.requestAnimationFrame(apply);
  };

  // Resync the live signal immediately, then re-check a few more times
  // shortly after, mirroring resyncMaterial()'s retry rationale but for the
  // keyboard opening rather than closing: the resize event that reports the
  // keyboard's full height can lag the open animation just as it can lag
  // the dismiss animation, and chat/video (layoutAdjustment.js) only ever
  // resize in response to a genuine layout.viewportHeight change — if the
  // first read after focus is stale (pre-keyboard), nothing else corrects
  // it, which otherwise leaves a gap under chat where the keyboard now sits.
  const resyncLive = () => {
    applyLive(Math.round(computeViewportHeight()));
    clearLiveRetries();
    LIVE_RESYNC_RETRY_DELAYS_MS.forEach((delay) => {
      const id = window.setTimeout(() => {
        applyLive(Math.round(computeViewportHeight()));
      }, delay);
      liveRetryTimeoutIds.push(id);
    });
  };

  // Freeze the material signal on focus of a text-like input, and force an
  // immediate (plus retried) resync of the live signal so chat/video pick up
  // the keyboard's shrink promptly even if the triggering resize event lags.
  const onFocusIn = (event) => {
    if (isTextInputTarget(event.target)) {
      materialFrozen = true;
      // A fresh focus supersedes any resync retries still pending from a
      // previous blur (e.g. tabbing quickly between two inputs).
      clearMaterialRetries();
      resyncLive();
    }
  };

  // Resume the material signal and resync it to the current height
  // immediately, then re-check a few more times shortly after to catch the
  // settled height once iOS's keyboard-dismiss animation finishes (see
  // MATERIAL_RESYNC_RETRY_DELAYS_MS above).
  const resyncMaterial = () => {
    materialFrozen = false;
    applyMaterial(Math.round(computeViewportHeight()));
    clearMaterialRetries();
    MATERIAL_RESYNC_RETRY_DELAYS_MS.forEach((delay) => {
      const id = window.setTimeout(() => {
        // Skip if a new focus re-froze material in the meantime.
        if (!materialFrozen) {
          applyMaterial(Math.round(computeViewportHeight()));
        }
      }, delay);
      materialRetryTimeoutIds.push(id);
    });
  };

  // iOS 26 Safari has a reported regression where visualViewport.offsetTop
  // does not reset to 0 after the on-screen keyboard is dismissed, leaving
  // the page visually panned/scrolled even though the app has no scrollable
  // body of its own (main.css sets `body { overflow: hidden }`). Forcing a
  // scroll-to-top at the moment the keyboard closes is the standard
  // mitigation reported for this bug and nudges Safari into recomputing its
  // internal offset. Retried on the same schedule as the height resyncs,
  // since the dismiss animation settling late is the same underlying
  // concern. See .kiro/specs/lesson-room-viewport-keyboard/design.md.
  let scrollResetRetryTimeoutIds = [];

  const clearScrollResetRetries = () => {
    scrollResetRetryTimeoutIds.forEach(id => window.clearTimeout(id));
    scrollResetRetryTimeoutIds = [];
  };

  const resetScrollOnKeyboardClose = () => {
    window.scrollTo(0, 0);
    clearScrollResetRetries();
    LIVE_RESYNC_RETRY_DELAYS_MS.forEach((delay) => {
      const id = window.setTimeout(() => {
        window.scrollTo(0, 0);
      }, delay);
      scrollResetRetryTimeoutIds.push(id);
    });
  };

  const onFocusOut = (event) => {
    if (isTextInputTarget(event.target)) {
      resyncMaterial();
      resyncLive();
      resetScrollOnKeyboardClose();
    }
  };

  // Safety net: a missed focusout (app switched away mid-edit, focused
  // element removed from the DOM, etc.) can't leave the material signal
  // permanently frozen — returning to the foreground always resumes it.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      resyncMaterial();
      resyncLive();
    }
  };

  const start = () => {
    apply();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onChange);
      window.visualViewport.addEventListener('scroll', onChange);
    }
    window.addEventListener('orientationchange', onChange);
    window.addEventListener('resize', onChange);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('visibilitychange', onVisibilityChange);
  };

  const stop = () => {
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
    materialFrozen = false;
    clearMaterialRetries();
    clearLiveRetries();
    clearScrollResetRetries();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', onChange);
      window.visualViewport.removeEventListener('scroll', onChange);
    }
    window.removeEventListener('orientationchange', onChange);
    window.removeEventListener('resize', onChange);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  return { start, stop, apply, onChange };
}

export default {
  install(Vue, { store } = {}) {
    if (!store) {
      throw new Error('VueViewportPlugin requires a Vuex store: Vue.use(Viewport, { store })');
    }
    Vue.prototype.$viewportTracker = createViewportTracker(store);
  }
};
